import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Bell, Construction, MessageSquare } from "lucide-react";
import WorkspaceTeamChatBlock from "@/components/equipo/WorkspaceTeamChatBlock";
import WorkspaceOnlineBadges from "@/components/equipo/WorkspaceOnlineBadges";
import ClientesAtencionPanel from "@/components/despacho/ClientesAtencionPanel";
import BntAsistenteMisNotasPanel from "@/components/asistente/BntAsistenteMisNotasPanel";
import BntAsistenteMisTareasPanel from "@/components/asistente/BntAsistenteMisTareasPanel";
import BntAsistenteDesignerChatTarget from "@/components/asistente/BntAsistenteDesignerChatTarget";
import { DespachoInfoCallout } from "@/components/despacho/DespachoUi";
import { Badge } from "@/components/ui/badge";
import { EQUIPO_CHAT_PATH } from "@/modules/equipo/equipoRoutes";
import { resolveDespachoViewerRole } from "@/lib/auxiliaryRoleGuide";
import { createBntAsistenteLinkCaptureHandler } from "@/modules/asistente/bntAsistenteNavigation";
import { useWorkspacePresenceList } from "@/lib/useWorkspacePresence";
import { useUiPreferences } from "@/lib/useUiPreferences";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { usePlatformDesignerBuyerPreview } from "@/lib/usePlatformDesignerBuyerPreview";

export const BNT_ASISTENTE_TABS = [
  { id: "interno", label: "Equipo" },
  { id: "mis-tareas", label: "Mis tareas" },
  { id: "mis-notas", label: "Mis notas" },
  { id: "alertas", label: "Alertas" },
  { id: "clientes", label: "Clientes" },
];

function CountPill({ icon: Icon, count, label }) {
  if (!count) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] font-medium text-slate-600"
      title={label}
      aria-label={`${label}: ${count}`}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {count}
    </span>
  );
}

function PriorityBadge({ prioridad }) {
  const tone =
    prioridad === "Crítico" || prioridad === "Urgente"
      ? "bg-rose-50 text-rose-900 border-rose-200"
      : prioridad === "Alta"
        ? "bg-amber-50 text-amber-950 border-amber-200"
        : "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <Badge variant="outline" className={`rounded-sm text-[9px] shrink-0 ${tone}`}>
      {prioridad}
    </Badge>
  );
}

function priorityLabel(priority) {
  const p = String(priority || "").toLowerCase();
  if (p === "alta") return "Alta";
  if (p === "baja") return "Baja";
  return "Media";
}

export default function BntAsistentePanelContent({
  user,
  summary,
  loading,
  tab,
  onTabChange,
  showHeaderCounts = true,
  onOpenAlert,
  onOpenInMainSystem,
  onPersonalNotesChanged,
  onAssignedTasksChanged,
  unreadTabs = {},
  chatPrivateFocus = { recipientId: "", pulsePrivate: false },
  designerChatTarget = null,
  onDesignerChatTargetChange,
}) {
  const openInMainSystem = onOpenInMainSystem || onOpenAlert;
  const isPlatformDesigner = usePlatformDesigner();
  const buyerPreviewActive = usePlatformDesignerBuyerPreview();
  const { buyerAsistenteClientesUnderConstruction } = useUiPreferences();
  /** Comprador (o vista previa comprador): oculta WhatsApp/borradores si el flag está activo. */
  const showClientesUnderConstruction = Boolean(
    buyerAsistenteClientesUnderConstruction
    && (!isPlatformDesigner || buyerPreviewActive),
  );
  const captureExternalLink = useMemo(
    () => createBntAsistenteLinkCaptureHandler(openInMainSystem),
    [openInMainSystem],
  );
  const headerCounts = {
    alerts: summary?.alertsCount || 0,
    chat: (summary?.teamChatHintCount || 0) + (summary?.clientMessagesCount || 0),
  };
  const personalDue = summary?.personalNoteAlerts || [];

  const todayKey = new Date().toISOString().slice(0, 10);
  const dismissStorageKey = `visor:dismiss-assigned-tasks-banner-${todayKey}`;
  const [assignedTasksBannerDismissed, setAssignedTasksBannerDismissed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem(dismissStorageKey) === "1",
  );

  useEffect(() => {
    if (assignedTasksBannerDismissed) {
      window.localStorage.setItem(dismissStorageKey, "1");
    }
  }, [assignedTasksBannerDismissed, dismissStorageKey]);

  const handleDismissAssignedTasksBanner = () => {
    setAssignedTasksBannerDismissed(true);
    window.localStorage.setItem(dismissStorageKey, "1");
  };

  const handleOpenInMainSystem = (href, event) => {
    event?.preventDefault?.();
    const path = href || "/";
    if (typeof openInMainSystem === "function") {
      openInMainSystem(path);
      return;
    }
    if (typeof window !== "undefined") {
      window.location.assign(path);
    }
  };

  const { items: presenceItems, loading: presenceLoading } = useWorkspacePresenceList({ enabled: Boolean(user) });

  return (
    <div
      className="flex flex-col h-full min-h-0"
      onClickCapture={captureExternalLink}
      data-testid="bnt-asistente-panel-content"
    >
      {showHeaderCounts ? (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 bg-slate-50/80 shrink-0">
          <CountPill icon={Bell} count={headerCounts.alerts} label="Alertas" />
          <CountPill icon={MessageSquare} count={headerCounts.chat} label="Mensajes" />
          <span
            className={`inline-block h-2.5 w-2.5 rounded-full ml-auto ${summary?.presenceOk ? "bg-emerald-500" : "bg-slate-300"}`}
            title={summary?.presenceOk ? `${summary.onlineCount} en línea` : "Sin presencia en línea"}
            aria-hidden
          />
        </div>
      ) : null}

      <div
        className="flex gap-1 px-3 py-1 border-b border-slate-100 shrink-0"
        role="tablist"
        aria-label="Secciones del asistente"
      >
        {BNT_ASISTENTE_TABS.map((item) => {
          const pendingByCount =
            (item.id === "mis-tareas" && Number(summary?.assignedTasksPendingCount || 0) > 0)
            || (item.id === "mis-notas" && Number(summary?.personalNotesDueCount || 0) > 0)
            || (item.id === "alertas" && Number(summary?.alertsCount || 0) > 0);
          const needsAttention =
            (Boolean(unreadTabs?.[item.id]) || pendingByCount) && tab !== item.id;
          return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={`flex-1 rounded-sm px-1.5 py-1 text-[10px] sm:text-[11px] font-medium leading-tight transition-colors ${
              tab === item.id
                ? "bg-[#0F2D5C] text-white"
                : needsAttention
                  ? "bg-rose-100 text-rose-900 border border-rose-300 animate-pulse"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
            onClick={() => onTabChange?.(item.id)}
            data-testid={`bnt-asistente-tab-${item.id}`}
            data-unread={needsAttention ? "true" : "false"}
            data-attention={needsAttention ? "true" : "false"}
          >
            {item.label}
            {item.id === "mis-notas" && summary?.personalNotesDueCount
              ? ` (${summary.personalNotesDueCount})`
              : ""}
            {item.id === "mis-tareas" && summary?.assignedTasksPendingCount
              ? ` (${summary.assignedTasksPendingCount})`
              : ""}
          </button>
          );
        })}
      </div>

      <div
        className="px-3 py-1.5 border-b border-slate-100 bg-white shrink-0"
        data-testid="bnt-asistente-presence-strip"
      >
        <WorkspaceOnlineBadges
          presenceItems={presenceItems}
          currentUserId={user?.id}
          loading={presenceLoading}
          testIdPrefix="bnt-asistente-online"
          emptyLabel="Nadie conectado en este momento."
        />
      </div>

      {tab === "interno" ? (
        <div
          className="flex-1 min-h-0 flex flex-col p-3 gap-1.5 overflow-hidden"
          data-testid="bnt-asistente-panel-interno"
        >
          <p className="text-[10px] text-slate-500 m-0 shrink-0 leading-snug">
            Chat del equipo ·{" "}
            {typeof openInMainSystem === "function" ? (
              <button
                type="button"
                className="underline font-medium text-[#0F2D5C]"
                onClick={() => openInMainSystem(EQUIPO_CHAT_PATH)}
                data-testid="bnt-asistente-open-equipo-chat"
              >
                pantalla completa
              </button>
            ) : (
              <Link to={EQUIPO_CHAT_PATH} className="underline font-medium text-[#0F2D5C]">
                pantalla completa
              </Link>
            )}
          </p>
          {isPlatformDesigner && !buyerPreviewActive ? (
            <BntAsistenteDesignerChatTarget
              value={designerChatTarget}
              onChange={onDesignerChatTargetChange}
            />
          ) : null}
          <div className="flex-1 min-h-0 min-w-0">
            <WorkspaceTeamChatBlock
              embedded
              pinComposer
              activeParticipantCount={summary?.onlineCount || 0}
              initialPrivateRecipientId={chatPrivateFocus?.recipientId || ""}
              highlightPrivateToggle={Boolean(chatPrivateFocus?.pulsePrivate)}
            />
          </div>
        </div>
      ) : (
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {summary?.assignedTasksBannerCount > 0 && tab !== "mis-tareas" && !assignedTasksBannerDismissed ? (
          <div
            className="mb-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-950"
            data-testid="bnt-asistente-assigned-tasks-banner"
          >
            <p className="m-0">
              Tiene <strong>{summary.assignedTasksBannerCount}</strong> tarea(s) pendiente(s) con el administrador.
              Revíselas en <strong>Mis tareas</strong>.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              <button
                type="button"
                className="text-[10px] font-medium text-[#0F2D5C] underline"
                onClick={() => onTabChange?.("mis-tareas")}
              >
                Abrir Mis tareas
              </button>
              <button
                type="button"
                className="text-[10px] text-slate-600 underline"
                onClick={handleDismissAssignedTasksBanner}
              >
                Cerrar por hoy
              </button>
            </div>
          </div>
        ) : null}

        {tab === "mis-tareas" ? (
          <BntAsistenteMisTareasPanel user={user} onTasksChanged={onAssignedTasksChanged} />
        ) : null}

        {tab === "mis-notas" ? (
          <BntAsistenteMisNotasPanel onNotesChanged={onPersonalNotesChanged} />
        ) : null}

        {tab === "clientes" ? (
          <div className="space-y-4" data-testid="bnt-asistente-panel-clientes">
            {showClientesUnderConstruction ? (
              <div
                className="rounded-sm border border-amber-200 bg-amber-50/90 px-4 py-10 text-center space-y-3"
                data-testid="bnt-asistente-clientes-under-construction"
                role="status"
              >
                <Construction className="h-8 w-8 text-amber-800 mx-auto" strokeWidth={1.6} aria-hidden />
                <p className="text-base font-semibold text-[#0F2D5C] m-0">En construcción</p>
                <p className="text-xs text-slate-600 m-0 max-w-sm mx-auto leading-relaxed">
                  La atención a clientes por WhatsApp y borradores sugeridos estará disponible pronto.
                </p>
              </div>
            ) : summary?.despachoActive ? (
              <>
                <DespachoInfoCallout>
                  WhatsApp Web anclado + borradores sugeridos. Pantalla completa:{" "}
                  {typeof openInMainSystem === "function" ? (
                    <button
                      type="button"
                      className="underline font-medium"
                      onClick={() => openInMainSystem("/despacho/mensajes")}
                      data-testid="bnt-asistente-open-mensajes"
                    >
                      Mensajes
                    </button>
                  ) : (
                    <Link to="/despacho/mensajes" className="underline font-medium">
                      Mensajes
                    </Link>
                  )}
                  .
                </DespachoInfoCallout>
                <div data-testid="bnt-asistente-client-inbox">
                  <ClientesAtencionPanel
                    embedded
                    despachoModuleActive
                    viewerRole={resolveDespachoViewerRole(user)}
                  />
                </div>
              </>
            ) : (
              <DespachoInfoCallout>
                El módulo <strong>Gestión Despacho</strong> no está activo. Use la pestaña{" "}
                <strong>Equipo</strong> para chat interno o active el módulo para mensajes con clientes.
              </DespachoInfoCallout>
            )}
          </div>
        ) : null}

        {tab === "alertas" ? (
          <div className="space-y-2" data-testid="bnt-asistente-panel-alertas">
            <p className="text-[11px] text-slate-600 m-0">
              {summary?.despachoActive
                ? "Resumen operativo del despacho (vencimientos, tareas y alertas) más avisos de Mis notas."
                : "Avisos de Mis notas y vista demostrativa. Active Gestión Despacho para alertas operativas."}
            </p>
            {personalDue.length > 0 ? (
              <div className="space-y-2" data-testid="bnt-asistente-alertas-personales">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 m-0">
                  Mis notas (hoy / vencidas)
                </p>
                <ul className="space-y-2 m-0 p-0 list-none">
                  {personalDue.map((row) => (
                    <li
                      key={row.id}
                      className="rounded-sm border border-amber-200 bg-amber-50/80 px-3 py-2 flex items-start justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 m-0">{row.title}</p>
                        {row.subtitle ? (
                          <p className="text-[10px] text-slate-500 m-0 mt-0.5 truncate">{row.subtitle}</p>
                        ) : null}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <PriorityBadge prioridad={row.prioridad || priorityLabel(row.priority)} />
                        <button
                          type="button"
                          className="text-[10px] text-[#0F2D5C] underline"
                          onClick={() => onTabChange?.("mis-notas")}
                        >
                          Abrir
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {loading ? (
              <p className="text-xs text-slate-500">Cargando alertas…</p>
            ) : (
              <ul className="space-y-2 m-0 p-0 list-none">
                {(summary?.alertItems || []).map((row) => (
                  <li
                    key={row.id}
                    className="rounded-sm border border-slate-200 bg-white px-3 py-2 flex items-start justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 m-0">{row.title}</p>
                      {row.subtitle ? (
                        <p className="text-[10px] text-slate-500 m-0 mt-0.5 truncate">{row.subtitle}</p>
                      ) : null}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <PriorityBadge prioridad={row.prioridad} />
                      <button
                        type="button"
                        className="text-[10px] text-[#0F2D5C] underline"
                        onClick={(e) => handleOpenInMainSystem(row.href, e)}
                        data-testid={`bnt-asistente-alert-open-${row.id}`}
                      >
                        Ver
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {!loading && !(summary?.alertItems || []).length && !personalDue.length ? (
              <p className="text-xs text-slate-500">Sin alertas pendientes.</p>
            ) : null}
          </div>
        ) : null}
      </div>
      )}

      {tab !== "interno" ? (
      <footer className="shrink-0 border-t border-slate-200 bg-white px-3 py-1.5 text-[10px] text-slate-500 text-center">
        {user?.email || user?.name || "Sesión activa"} · minimice para seguir en Visor Fiscal
      </footer>
      ) : null}
    </div>
  );
}
